import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { showSnackbar } from 'helpers/snackbar';

import { StatusPill } from 'ds';
import { getWorkOrderStatusPillProps } from 'ux/Admin/Support/workOrderStatusConfig';

import { media } from 'context';
import { APP_BANNER_HEIGHT, Icon, Layout, LoadingScreen, PageLayout, PillButton, Text, TextButton } from 'ds';
import { formatUsd } from 'helpers';
import { CUSTOMER_SUPPORT_PATH, TENANT_ADMIN_PAYMENTS_PATH } from 'routes';
import { formatDate } from 'shared';
import { actions } from 'store/AIAssistant';
import { selectCustomerSupportTickets } from 'store/AIAssistant/selectors';
import { selectUIState } from 'store/UI/selectors';
import { selectUserId } from 'store/User/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import SupportTicketWorkOrderPageFeedbackCard from './SupportTicketWorkOrderPageFeedbackCard';
import { approveWorkOrder, getCustomerSupportTickets } from './requests';

interface Props {}

const SupportTicketWorkOrderPage: React.FC<Props> = () => {
  const { appBannerType } = useAppSelector(selectUIState);
  const dispatch = useAppDispatch();
  const { prevPathname } = useAppSelector(selectUIState);
  const { pathname } = useLocation();
  const history = useHistory();
  const { id: orderIdStr } = useParams<{ id: string }>();
  const [requestInProgress, setRequestInProgress] = useState(false);
  const hasFetchedRef = useRef<number | null>(null);
  const { isMobile } = useContext(media);

  // Extract support ticket id and order id from pathname: /app/requests/629/orders/34
  const pathParts = pathname.split('/');
  const supportTicketIdIndex = pathParts.findIndex(part => part === 'requests') + 1;
  const supportTicketIdStr = pathParts[supportTicketIdIndex];
  const orderId = parseInt(orderIdStr, 10);
  const userId = useAppSelector(selectUserId);

  const supportTicketId = parseInt(supportTicketIdStr, 10);
  const supportTickets = useAppSelector(selectCustomerSupportTickets);
  const supportTicket = supportTickets.find(ticket => ticket.id === supportTicketId);
  const workOrder = supportTicket?.work_orders.find(order => order.id === orderId);

  useEffect(() => {
    // Recompute supportTicket inside useEffect to avoid stale closure
    const currentSupportTicket = supportTickets.find(ticket => ticket.id === supportTicketId);

    // Reset fetch tracking if supportTicketId changes
    if (hasFetchedRef.current !== supportTicketId) {
      hasFetchedRef.current = null;
    }

    // Fetch support tickets if they're not in the store (e.g., after login redirect)
    // Only fetch once per supportTicketId
    const condition1 = hasFetchedRef.current !== supportTicketId;
    const condition2 = supportTickets.length === 0;
    const condition3 = !currentSupportTicket;
    const shouldFetch = condition1 && (condition2 || condition3);

    if (shouldFetch) {
      hasFetchedRef.current = supportTicketId;
      const fetchSupportTickets = async () => {
        try {
          const { data } = await getCustomerSupportTickets();
          dispatch(actions.setSupportTickets(data));
        } catch (error) {
          console.error('Failed to fetch support tickets:', error);
        }
      };
      fetchSupportTickets();
    }
  }, [dispatch, supportTickets.length, supportTicketId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMobile && workOrder) {
      history.push(`${CUSTOMER_SUPPORT_PATH}/${supportTicketId}/details?order_id=${workOrder.id}`);
    }
  }, [isMobile, workOrder, history, supportTicketId]);

  if (!workOrder) {
    return <LoadingScreen />;
  }

  const handleApprove = () => {
    setRequestInProgress(true);

    approveWorkOrder({ supportTicketId, workOrderId: workOrder.id })
      .then(({ data: updatedWorkOrder }) => {
        dispatch(actions.updateSupportTicketWorkOrder({ supportTicketId, workOrder: updatedWorkOrder }));
        showSnackbar({ message: 'Work order approved.' });
      })
      .catch(_error => {
        showSnackbar({ message: 'An error occurred.', negative: true });
      })
      .finally(() => {
        setRequestInProgress(false);
      });
  };

  const {
    po_number,
    vendor,
    approved_at,
    approver,
    status,
    payment_id,
    feedback,
    description,
    work_order_line_items,
    task,
    total_amount_cents: workOrderTotalAmountCents
  } = workOrder;

  const userFeedback = feedback.find(f => f.user_id === userId);

  const backLink =
    prevPathname && prevPathname !== pathname
      ? { href: prevPathname }
      : { href: `${CUSTOMER_SUPPORT_PATH}/${supportTicketId}` };

  const formattedScheduledDate =
    status === 'scheduled' && task?.scheduled_date
      ? formatDate({ date: task.scheduled_date, format: 'SHORTEST' })
      : null;

  return (
    <PageLayout
      type="default"
      color="blue-50"
      title="Order Details"
      backLink={backLink}
      navBarTopOffset={appBannerType ? APP_BANNER_HEIGHT : 0}
    >
      <Layout width="100%" justify="center" direction="column" gap={16}>
        {status === 'completed' && (
          <SupportTicketWorkOrderPageFeedbackCard
            supportTicketId={supportTicketId}
            workOrderId={workOrder.id}
            userFeedback={userFeedback}
          />
        )}
        <Layout width={724} color="white" direction="column" borderRadius={16}>
          <Layout direction="column" gap={36} padding={24} borderTopLeftRadius={16} borderTopRightRadius={16}>
            <Layout direction="row" justify="space-between">
              <Layout direction="column" gap={8}>
                <Text size="headline-md">{po_number}</Text>
                {!!vendor && <Text size="body-md">{vendor.name}</Text>}
              </Layout>
              <Layout direction="column" gap={8}>
                <StatusPill
                  {...getWorkOrderStatusPillProps(status, formattedScheduledDate)}
                  size="md"
                  showDot={false}
                />
                {payment_id && (
                  <TextButton
                    text="View receipt"
                    color="blue-500"
                    textSize="body-sm"
                    semibold={false}
                    onClick={() => {
                      history.push(`${TENANT_ADMIN_PAYMENTS_PATH}?payment_id=${payment_id}`);
                    }}
                  />
                )}
              </Layout>
            </Layout>
            <Layout direction="column" gap={16}>
              <Layout paddingBottom={24} direction="column" borderBottom>
                <Text size="body-xs" semibold>
                  Description
                </Text>
                <Text size="body-sm">{description}</Text>
              </Layout>
              <Layout paddingBottom={16} direction="column" borderBottom gap={16}>
                <Text size="body-sm" semibold>
                  Items
                </Text>
                {work_order_line_items.map(lineItem => {
                  return (
                    <Layout direction="column" gap={4}>
                      <Layout direction="row" justify="space-between">
                        <Text size="body-xs" semibold>
                          {lineItem.description}
                        </Text>
                        <Text size="body-xs" semibold>
                          {lineItem.total_amount_cents != null ? formatUsd(lineItem.total_amount_cents / 100) : 'TBD'}
                        </Text>
                      </Layout>
                      {lineItem.amount_cents_hourly != null && (
                        <Text size="body-xs" color="gray-700">
                          ({formatUsd(lineItem.amount_cents_hourly / 100)}/hr)
                        </Text>
                      )}
                    </Layout>
                  );
                })}
              </Layout>
              <Layout direction="column" gap={4} marginBottom={16}>
                <Layout direction="row" justify="space-between">
                  <Text size="body-sm" semibold>
                    {workOrderTotalAmountCents != null ? 'Total' : 'Est. total'}
                  </Text>
                  <Text size="body-sm" semibold>
                    {workOrderTotalAmountCents != null ? formatUsd(workOrderTotalAmountCents / 100) : 'TBD'}
                  </Text>
                </Layout>
                {workOrderTotalAmountCents == null && (
                  <Text size="body-xs" color="gray-700">
                    Final total will be calculated once labor hours are confirmed.
                  </Text>
                )}
              </Layout>
            </Layout>
          </Layout>
          {status === 'needs_approval' ? (
            <Layout
              padding={24}
              direction="row"
              color="gold-100"
              borderBottomLeftRadius={16}
              borderBottomRightRadius={16}
            >
              <Layout direction="column" justify="space-between" align="flex-start">
                <Text size="body-xs" bold>
                  By approving this work order, you agree to the pricing shown above.
                </Text>
                <Text size="body-xs" color="gray-700">
                  Some items have a fixed cost, while others are billed hourly. For hourly items, you are approving the
                  hourly rate; the final amount will reflect the actual hours worked. All labor-related charges will be
                  processed once the work is completed. Approval confirms acceptance of these terms.
                </Text>
              </Layout>
              <Layout width={138} minWidth={138} marginLeft={48}>
                <PillButton
                  size="lg"
                  text="Approve order"
                  type="primary"
                  fullWidth
                  onClick={handleApprove}
                  disabled={requestInProgress}
                />
              </Layout>
            </Layout>
          ) : (
            <Layout padding={24}>
              <Layout
                color={status === 'canceled' ? 'gray-50' : 'green-100'}
                borderRadius={8}
                paddingX={12}
                paddingY={8}
                gap={8}
                align="center"
              >
                <Layout
                  width={16}
                  height={16}
                  justify="center"
                  align="center"
                  color={status === 'canceled' ? undefined : 'green-700'}
                  borderRadius={1000}
                  paddingX={2}
                  paddingY={2}
                >
                  <Icon
                    name={status === 'canceled' ? 'noSymbol' : 'checkmark'}
                    size={status === 'canceled' ? 'xs' : 11}
                    color={status === 'canceled' ? 'gray-900' : 'white'}
                    stroke={2}
                  />
                </Layout>
                <Text size="body-sm">
                  {status === 'canceled'
                    ? 'Canceled on'
                    : `Approved by ${approver?.firstname} ${approver?.lastname} on`}{' '}
                  {status === 'canceled'
                    ? formatDate({ date: task?.canceled_at ?? '', format: 'MED_WITH_YEAR' })
                    : formatDate({ date: approved_at ?? '', format: 'MED_WITH_YEAR' })}
                </Text>
              </Layout>
            </Layout>
          )}
        </Layout>
      </Layout>
    </PageLayout>
  );
};

export default SupportTicketWorkOrderPage;
